export interface ThrottledFunc<T extends (...args: unknown[]) => unknown> {
  (...args: Parameters<T>): void
  cancel(): void
  flush(): void
}

/**
 * Creates a throttled version of the provided function that only invokes
 * at most once per `wait` milliseconds.
 *
 * By default both `leading` and `trailing` are true.
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
  options?: {leading?: boolean; trailing?: boolean}
): ThrottledFunc<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null
  let lastCallTime: number | null = null
  const leading = options?.leading ?? true
  const trailing = options?.trailing ?? true

  function invoke() {
    if (lastArgs) {
      const args = lastArgs
      lastArgs = null
      lastCallTime = Date.now()
      func(...args)
    }
  }

  const throttled = ((...args: Parameters<T>) => {
    const now = Date.now()
    lastArgs = args

    if (lastCallTime === null && !leading) {
      lastCallTime = now
    }

    const remaining = lastCallTime === null ? 0 : wait - (now - lastCallTime)

    if (remaining <= 0) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      lastCallTime = now
      if (leading) {
        func(...args)
        lastArgs = null
      } else if (trailing) {
        timeoutId = setTimeout(() => {
          timeoutId = null
          invoke()
        }, wait)
      }
    } else if (trailing && timeoutId === null) {
      timeoutId = setTimeout(() => {
        timeoutId = null
        invoke()
      }, remaining)
    }
  }) as ThrottledFunc<T>

  throttled.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    lastArgs = null
    lastCallTime = null
  }

  throttled.flush = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
      invoke()
    }
  }

  return throttled
}
